import type { NextPage } from "next";
import Head from "./head";
import AllLayout from "./all-layout";
import LocalSearchSwitch from "./local-search-switch";
import SearchListing from "./search-listing";
import shared from "../data/js/shared";
import { useState } from "react";
import { useCategory, useProduct, useRouterContext, useSearch } from "../contexts";
import GoogleAdsenseTag from "./embed-tags/google-adsense";
import { useScrollRestoration } from "../utils/useScrollRestoration";
import { useScrollClassToggle } from "./browse-category/useScrollClassToggle";
import { useBrowseListings } from "./browse-category/useBrowseListings";
import { parseListingSort, sortHeading } from "../utils/listing-sort";
import type { Product } from "../utils/product/product-types";

const { homepageTitle, metaDescription, categoryHeading } = shared.browse;

interface BrowsePageProps {
  /**
   * Nothing here comes from the server any more. `pages/index.tsx` fetches no
   * data at all: the grid is populated by `useBrowseListings` and the showcase
   * rows by `useHomeShowcases`, both client-side. The prop is kept only because
   * `CompositeAppProvider` spreads `pageProps` and other callers still pass one.
   */
  currentList?: Product[];
  currentCategoryGroupId?: number;
}

const Home: NextPage<BrowsePageProps> = (props) => {
  const { searchEmpty } = useSearch();
  const { getCategoryById } = useCategory();
  const { currentList, replaceCurrentList } = useProduct();
  const { currentCategoryGroupId } = props;
  // Home reads one URL param: a picked sort. It passed `{}` here, so
  // ?sortBy= changed the URL and nothing else (prod, 2026-10-08).
  const { query, isReady } = useRouterContext();
  const sortParam = query.sortBy;

  // Pagination state for infinite scroll + restoration
  const [page, setPage] = useState(1);
  const [done, setDone] = useState(false);

  useScrollRestoration({ page, setPage, done, setDone });
  useScrollClassToggle('scrolling');

  // Single owner of listing population (same controller as the category page).
  // Home has no category, so `isHome` switches it to the home-listings endpoint.
  useBrowseListings({
    isHome: true,
    initialList: props.currentList || [],
    effectiveId: 0,
    isSubcategory: false,
    urlParams: sortParam ? { sortBy: sortParam } : {},
    ready: isReady,
    setPage,
    setDone,
  });

  // The home component only ever renders the home feed (currentCategoryGroupId is
  // always undefined for `/`), but keep the category-aware heading for safety.
  const category = currentCategoryGroupId ? getCategoryById(currentCategoryGroupId) : null;
  const categoryName = category?.label || '';
  const heading = currentCategoryGroupId
    ? <h2 className="browse-heading">{categoryHeading}{categoryName}</h2>
    : <h2 className="browse-heading">{sortHeading(sortParam)}</h2>;

  return (
    <AllLayout header={true} search={true} nav={true} showAd={true}>
      <Head
        title={currentCategoryGroupId ? `${categoryHeading}${categoryName} | Sellwild` : homepageTitle}
        description={currentCategoryGroupId ? `${categoryHeading}${categoryName} listings on Sellwild` : metaDescription}
      />
      <article className="page same-color" data-testid="browse-page">
        <LocalSearchSwitch />
        <SearchListing
          searchEmpty={searchEmpty}
          heading={heading}
          page={page}
          setPage={setPage}
          done={done}
          setDone={setDone}
          isHome
          sortable
        />
        <GoogleAdsenseTag />
      </article>
    </AllLayout>
  );
};

export default Home;
